/**
 * Helpers for applying GET /v1/config fields in the panel.
 */

import type { WidgetConfig, WidgetFeatures } from '../types/api';
import type { WidgetChannel } from '../types/bridge';

/** Feature flags from config — defaults match MVP (text-only). */
export const DEFAULT_FEATURES: WidgetFeatures = {
  file_upload: false,
  voice: false,
  pre_chat_form: false,
};

export function resolveFeatures(
  features: WidgetFeatures | undefined | null,
): WidgetFeatures {
  return {
    file_upload: Boolean(features?.file_upload),
    voice: Boolean(features?.voice),
    pre_chat_form: Boolean(features?.pre_chat_form),
  };
}

/** Non-empty question strings from GET /v1/config.popular_questions. */
export function resolvePopularQuestions(
  questions: WidgetConfig['popular_questions'] | undefined | null,
): string[] {
  if (!questions?.length) return [];
  return questions
    .map((item) => (item?.question || '').trim())
    .filter(Boolean);
}

/**
 * Whether the host should show the chat bubble.
 * `widget_status` is the first indicator — AND more flags here as they land.
 */
export function isLauncherEnabled(
  config: Pick<WidgetConfig, 'widget_status'>,
): boolean {
  return config.widget_status !== false;
}

/** True when Gateway requires HMAC identity binding. */
export function isIdentityVerificationEnabled(
  value: string | undefined | null,
): boolean {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized !== '' && normalized !== 'disabled' && normalized !== 'off';
}

/**
 * Best-effort embedding host origin (customer site).
 * Prefer ancestor / referrer over the panel iframe origin — used for
 * soft allowlist checks and the `X-Embed-Origin` header for Gateway.
 */
export function resolveHostOrigin(): string {
  if (typeof window === 'undefined') return '';

  try {
    const ancestors = (
      window as Window & { location: Location & { ancestorOrigins?: DOMStringList } }
    ).location.ancestorOrigins;
    if (ancestors && ancestors.length > 0 && ancestors[0]) {
      return new URL(ancestors[0]).origin;
    }
  } catch {
    // ignore
  }

  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // ignore
  }

  return window.location.origin;
}

/**
 * Headers for Gateway allowlist.
 * Web: `X-Embed-Origin` (customer site). Mobile: `X-App-Bundle-Id` (iOS/Android
 * bundle) — never the WebView page origin. GET /v1/config has no body so the
 * bundle header is required there; session/chat may send `app_bundle_id` in
 * the JSON body instead (header on those calls is optional).
 */
export function embedGatewayHeaders(context: {
  channel: WidgetChannel;
  bundleId?: string;
  origin?: string;
}): Record<string, string> {
  if (context.channel === 'mobile_webview') {
    const bundle = (context.bundleId || '').trim();
    return bundle ? { 'X-App-Bundle-Id': bundle } : {};
  }

  const origin = context.origin ?? resolveHostOrigin();
  if (!origin) return {};
  return { 'X-Embed-Origin': origin };
}

/** @deprecated use embedGatewayHeaders — kept for call sites that only need web origin. */
export function embedOriginHeaders(
  origin: string = resolveHostOrigin(),
): Record<string, string> {
  return embedGatewayHeaders({ channel: 'web_widget', origin });
}

/**
 * Soft client allowlist (Gateway still enforces).
 * Web → origins. Mobile → mobile_bundles. Empty list → skip.
 */
export function allowlistBlockReason(
  config: Pick<WidgetConfig, 'allowlist'>,
  context: {
    channel: WidgetChannel;
    origin?: string;
    bundleId?: string;
  },
): string | null {
  if (context.channel === 'mobile_webview') {
    const bundles = config.allowlist?.mobile_bundles ?? [];
    if (bundles.length === 0) return null;
    const bundle = (context.bundleId || '').trim();
    if (!bundle) {
      return "This app is not allowlisted for this avatar (missing bundle id).";
    }
    const allowed = bundles.some((item) => item.trim() === bundle);
    if (allowed) return null;
    return `App '${bundle}' is not allowlisted for this avatar.`;
  }

  const origins = config.allowlist?.origins ?? [];
  if (origins.length === 0) return null;
  const origin = context.origin ?? resolveHostOrigin();
  if (!origin) return null;

  const normalized = origin.replace(/\/$/, '').toLowerCase();
  const allowed = origins.some(
    (item) => item.replace(/\/$/, '').toLowerCase() === normalized,
  );

  if (allowed) return null;
  return `Origin '${origin}' is not allowlisted for this avatar.`;
}
